import React, { useEffect, useState } from 'react';
import { timeOffRequestsAPI } from '../../services/api';

export interface CalendarEntry {
  id: number;
  start_date: string;
  end_date: string;
  request_type: string;
  status: string;
  user?: {
    full_name: string;
  };
}

interface TimeOffCalendarViewProps {
  isDark: boolean;
  cardClass: string;
  arrowLeftIcon: React.ReactNode;
  chevronRightIcon: React.ReactNode;
  // Optional props for controlled mode
  entries?: CalendarEntry[];
  year?: number;
  month?: number;
  onMonthChange?: (year: number, month: number) => void;
}

const TimeOffCalendarView: React.FC<TimeOffCalendarViewProps> = ({
  isDark,
  cardClass,
  arrowLeftIcon,
  chevronRightIcon,
  entries,
  year,
  month,
  onMonthChange,
}) => {
  const now = new Date();
  const [calendarYear, setCalendarYear] = useState(year ?? now.getFullYear());
  const [calendarMonth, setCalendarMonth] = useState(month ?? now.getMonth() + 1);
  const [calendarDay, setCalendarDay] = useState(now.getDate());
  const [calendarData, setCalendarData] = useState<CalendarEntry[]>(entries || []);
  const [calendarLoading, setCalendarLoading] = useState(false);

  useEffect(() => {
    if (entries) {
      setCalendarData(entries);
    } else {
      const fetchCalendar = async () => {
        setCalendarLoading(true);
        try {
          const response = await timeOffRequestsAPI.calendar(
            calendarYear,
            calendarMonth,
            calendarDay,
          );
          setCalendarData(response.data.calendar_entries || []);
        } catch (error) {
          console.error('Failed to fetch calendar data:', error);
        } finally {
          setCalendarLoading(false);
        }
      };

      fetchCalendar();
    }
  }, [entries, calendarYear, calendarMonth, calendarDay]);

  const prevMonth = () => {
    if (onMonthChange) {
      if (calendarMonth === 1) {
        onMonthChange(calendarYear - 1, 12);
      } else {
        onMonthChange(calendarYear, calendarMonth - 1);
      }
    } else {
      if (calendarMonth === 1) {
        setCalendarMonth(12);
        setCalendarYear(calendarYear - 1);
      } else {
        setCalendarMonth(calendarMonth - 1);
      }
    }
  };

  const nextMonth = () => {
    if (onMonthChange) {
      if (calendarMonth === 12) {
        onMonthChange(calendarYear + 1, 1);
      } else {
        onMonthChange(calendarYear, calendarMonth + 1);
      }
    } else {
      if (calendarMonth === 12) {
        setCalendarMonth(1);
        setCalendarYear(calendarYear + 1);
      } else {
        setCalendarMonth(calendarMonth + 1);
      }
    }
  };

  const getCalendarDays = () => {
    const firstDay = new Date(calendarYear, calendarMonth - 1, 1).getDay();
    const daysInMonth = new Date(calendarYear, calendarMonth, 0).getDate();
    return { firstDay, daysInMonth };
  };

  const getLeaveOnDay = (day: number) => {
    const dateStr = `${calendarYear}-${String(calendarMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return calendarData.filter((entry) => {
      return entry.start_date <= dateStr && entry.end_date >= dateStr;
    });
  };

  const calendarColorMap: Record<string, string> = {
    vacation: isDark
      ? 'bg-indigo-500/30 text-indigo-300'
      : 'bg-indigo-100 text-indigo-700',
    sick_leave: isDark
      ? 'bg-red-500/30 text-red-300'
      : 'bg-red-100 text-red-700',
    sick: isDark ? 'bg-red-500/30 text-red-300' : 'bg-red-100 text-red-700',
    personal: isDark
      ? 'bg-blue-500/30 text-blue-300'
      : 'bg-blue-100 text-blue-700',
    emergency: isDark
      ? 'bg-amber-500/30 text-amber-300'
      : 'bg-amber-100 text-amber-700',
  };

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  return (
    <div className={`${cardClass} rounded-2xl p-4`}>
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={prevMonth}
          className={`p-2 rounded-xl transition-all ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
        >
          {arrowLeftIcon}
        </button>
        <h2
          className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
        >
          {monthNames[calendarMonth - 1]} {calendarYear}
        </h2>
        <button
          onClick={nextMonth}
          className={`p-2 rounded-xl transition-all ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
        >
          {chevronRightIcon}
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div
            key={d}
            className={`text-center text-xs font-semibold py-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
          >
            {d}
          </div>
        ))}
      </div>

      {calendarLoading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: getCalendarDays().firstDay }).map((_, i) => (
            <div
              key={`empty-${i}`}
              className={`rounded-lg min-h-[80px] ${isDark ? 'bg-slate-800/30' : 'bg-slate-50'}`}
            />
          ))}

          {Array.from({ length: getCalendarDays().daysInMonth }).map((_, i) => {
            const day = i + 1;
            const leaves = getLeaveOnDay(day);
            const isToday =
              calendarYear === now.getFullYear() &&
              calendarMonth === now.getMonth() + 1 &&
              day === now.getDate();

            return (
              <div
                key={day}
                className={`border rounded-lg p-1.5 min-h-[80px] ${
                  isToday
                    ? isDark
                      ? 'border-violet-500/50 bg-violet-500/10'
                      : 'border-violet-300 bg-violet-50'
                    : isDark
                      ? 'border-slate-700/50 bg-slate-800/30'
                      : 'border-slate-200 bg-white'
                }`}
              >
                <div
                  className={`text-right text-xs font-bold mb-1 ${
                    isToday
                      ? isDark
                        ? 'text-violet-400'
                        : 'text-violet-600'
                      : isDark
                        ? 'text-slate-400'
                        : 'text-slate-500'
                  }`}
                >
                  {day}
                </div>
                <div className="flex flex-wrap gap-0.5">
                  {leaves.map((leave) => (
                    <span
                      key={leave.id}
                      className={`px-1.5 py-0.5 text-xs rounded truncate max-w-full border ${leave.status === 'pending' ? (isDark ? 'border-2 border-yellow-500' : 'border-2 border-yellow-400') : 'border-transparent'} ${calendarColorMap[leave.request_type] || (isDark ? 'bg-slate-600 text-slate-300' : 'bg-slate-200 text-slate-600')}`}
                      title={`${leave.user?.full_name} — ${leave.request_type}`}
                    >
                      {leave.user?.full_name}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TimeOffCalendarView;
